
import { defineComponent } from 'vue';
import axios from "axios";
import UAParser from 'ua-parser-js';
import wasm from '../components/wasm.vue';
import createDittoSenderModule from 'ditto-sender-wasm-release';
import InlineTextLoader from '../components/InlineTextLoader.vue';
import Download from '../components/DownloadInstructions.vue'
import Sharing from '../components/SharingInstructions.vue'

// We need to declare the sender 
// outside of Vue so that it isnt a proxy
let dittoSender = null as any;
let DittoSenderModule = null as any;
let SQLogLevel = null as any;
let DittoSender = null as any;
let DTSenderStatus = null as any;

const __default__ = defineComponent({
    name: 'ConnectView',
    data: () => ({
        roomCodeInput: '' as string,
        passwordInput: '' as string,
        isPasswordInputVisible: false as boolean,
        config: null as any | null,
        foundBranding: null as any | null,
        userAgent: null as null | any,
        osName: '' as string,
        organizationId: '' as string,
        organizationName: '' as string,
        foundRoomName: '' as string,
        roomReceivers: {} as any,

        // Debounces
        roomIconVisibilityTimeout: null as any,
        lookupRoomTimeout: null as any,
            
        // Boolean flags
        isRoomFoundResponse: null as null | boolean,
        isWasmBusy: false as boolean,
        isPasswordRequired: false as boolean,
        isContinueOnBrowserFlagged: false as boolean,
        isRoomLookupBusy: false as boolean,
        didUserDisconnect: false as boolean,
        didUserConnect: false as boolean,
        isInitialRoomCodeFetch: false as boolean,
        receiverType: 'other' as string,
        isSelectAllEnabled: false as boolean,

        // WASM
        address: '',
        appState: '' as string,
        captureStream: null as any,
        streamRecorder: null as any,
        isAddressValid: false,
        failedAllQueries: false as boolean,
        failedAllQueriesID: '' as string,
        password: '',
        receiverName: ['Receiver Name'] as string[],
        windowWidth: window.innerWidth,
        UseMediaStreamTrackProcessor: false,
        videoReader: null as any,
        audioReader: null as any,
        videoWidth: 0,
        videoHeight: 0,
        videoInfoNeedsSent: false,
        audioInfoNeedsSent: false,
        audioConverterStream: null as any,
        shouldBeRunning: false,
        videoWorker: null as any,
        audioWorker: null as any,
        defaultVideoEncoder: 'avc1.42E028',
        videoEncoderToUse: null as any,
        FindNextAvailableVideoEncoder: false,
        shouldRestart: false
    }),


    props: [
        'brandingAccentColor',
        'isConnectView',
        'isRoomCodeView',
        'isDownloadView',
        'isSharingView',
        'isRoomCodeConnectView',
        'isOrganizationConnectView'
    ],

    computed: {
        loadWasm(){
            return this.isSupportedBrowser && (this.osName === "Mac OS" || this.osName === "Windows" || this.osName === "Chromium OS" || this.osName === "Linux");
        },

        isSupportedBrowser() {
            return this.userAgent.browser.name === "Chrome" || this.userAgent.browser.name === "Edge";
        },

        isSSPReceiver() {
            return this.receiverType === "SSP";
        },

        isRoomCodeBusy() {
            return this.appState == 'Connecting' || this.isRoomLookupBusy;
        },

        didUserDisconnectUnepectedly() {
            return this.appState != "Connected" && this.didUserConnect && !this.didUserDisconnect;
        },
        
        isConnectButtonDisabled() {
            return this.appState != 'Ready' || !this.isAddressValid;
        },

        connectButtonDisabled() {
            return !this.isAddressValid;
        },

        connectFormDisabled() {
            return this.appState != 'Ready'
        },

        // Check brightness of accent color to 
        // determine if we should use black or white text
        isBrightAccentColor() {
            const color = this.brandingAccentColor ? this.brandingAccentColor : [34,107,216];
            const brightness =  (color[0] * 299 +
                                color[1]* 587 +
                                color[2] * 114) / 1000;
            return brightness > 140;
        },

        // Convert RGB from Colorthief to HSL
        // that we can then darken for :hover & :active
        getBrandingColorHslArray() {
            const color = this.brandingAccentColor ? this.brandingAccentColor : [34,107,216];
            const r = color[0] / 255,
                  g = color[1] / 255,
                  b = color[2] / 255;

            // Find greatest and smallest channel values
            let cmin = Math.min(r,g,b),
                cmax = Math.max(r,g,b),
                delta = cmax - cmin,
                h = 0,
                s = 0,
                l = 0;

            if (delta == 0)
                h = 0;
            else if (cmax == r)
                h = ((g - b) / delta) % 6;
            else if (cmax == g)
                h = (b - r) / delta + 2;
            else
                h = (r - g) / delta + 4;
                h = Math.round(h * 60);
            if (h < 0)
                h += 360;

            l = (cmax + cmin) / 2;

            // Calculate saturation
            s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
                
            // Multiply l and s by 100
            s = +(s * 100).toFixed(1);
            l = +(l * 100).toFixed(1);
            
            return {
                h: h,
                s: s,
                l: l
            };
        },
        getBrandingColorHslCSV() {
            return `${ this.getBrandingColorHslArray.h },${ this.getBrandingColorHslArray.s }%,${ this.getBrandingColorHslArray.l }%`;
        },

        getBrandingColorHslNormal() {
            return "hsl(" + this.getBrandingColorHslArray.h + "," + this.getBrandingColorHslArray.s + "%," + this.getBrandingColorHslArray.l + "%)"
        },

        getBrandingColorHslDark() {
            return "hsl(" + this.getBrandingColorHslArray.h + "," + this.getBrandingColorHslArray.s + "%," + (this.getBrandingColorHslArray.l - 5) + "%)"
        },
        
        getBrandingColorHslDarker() {
            return "hsl(" + this.getBrandingColorHslArray.h + "," + this.getBrandingColorHslArray.s + "%," + (this.getBrandingColorHslArray.l - 10) + "%)"
        },

        getBrandingTextColor() {
            return this.isBrightAccentColor ? 'rgba(0,0,0,0.95)' : 'white';
        },

        receiverSelectAllText() {
            if(this.roomReceivers.filter((x: any) => !x.sspQueryFailed).length == this.roomReceivers.filter((x: any) => x.specificConnect).length) {
                return 'Deselect All';
            }
            else {
                return 'Select All';
            }
        },

        isSmallScreen(): boolean {
            if(this.windowWidth < 770) {
                return true;
            }
            return false;
        }
    },

    methods: {

        senderFailedAllQueries(){
            console.log('senderFailedAllQueries')
            this.failedAllQueries = true;
            this.failedAllQueriesID = this.foundBranding.roomId;

            this.appState = 'Ready';
            this.isRoomLookupBusy = false;
        },

        continueOnBrowserHandler() {
            this.$router.push(`/${ this.address }`);
            this.isContinueOnBrowserFlagged = true;
            // Only try to autoconnect if we actually have a code entered
            if(this.isAddressValid) {
                this.connect();
            }
        },

        continueToDownloadHandler() {
            // this.viewState = 'download';
            this.$router.push('/info/download');
        },

        onRoomCodeInput(e: any){ 
            const val = e.target.value.toLowerCase();
            this.$emit("onRoomCodeInput", val);
        },

        toggleIsPasswordInputVisible() {
            this.isPasswordInputVisible = !this.isPasswordInputVisible;
        },

        lookupRoom(e?: any, debounceLookup?: boolean) {
            if(e) this.address = e.target.value;

            this.isRoomFoundResponse = null;

            if(this.lookupRoomTimeout) {
                clearTimeout(this.lookupRoomTimeout);
                this.lookupRoomTimeout = null;
            }
            
            if(!this.address.length) return;

            this.lookupRoomTimeout = setTimeout(()=> {
                const body = { 
                    "roomCode": this.address,
                    "orgID": this.isOrganizationConnectView && this.organizationId ? this.organizationId : null
                };
                let host = window.location.host;
                let hostSplit = host.split('.');
                let url = `api.${hostSplit[hostSplit.length - 2]}.${hostSplit[hostSplit.length - 1]}`;
                this.isRoomLookupBusy = true;
                axios.post(`https://${url}/api/rooms/lookup/`, body)
                    .then((response: any)=>{
                        this.isRoomLookupBusy = false;
                        this.isRoomFoundResponse = true;
                        this.foundBranding = response.data;
                        this.foundRoomName = this.foundBranding.name;
                        this.receiverType = response.data && response.data.deviceType ? response.data.deviceType : "other";

                        // Emit branding so we can apply the 
                        // background, logo & generate a color palette
                        this.$emit('roomFound', this.foundBranding);

                        // TODO: Make sure we respond correctly to all error
                        // -- We did this on the old portal - error codes we were handling are below

                        // -- Google Analytics -----
                        // if (typeof ga !== "undefined" && ga !== null) {
                        //      ga('send', 'pageview', '/connect/info');
                        // }
                    })
                    .catch((error: any)=> {
                        this.isRoomLookupBusy = false;
                        this.isRoomFoundResponse = false;
                        setTimeout(()=> { this.isRoomFoundResponse = null }, 1500)
                        console.log("We have an error: ", error.toJSON());
                    })
                ;
            }, debounceLookup ? 750 : 0);

            // -- MOCK OUT OF THE ROOM RESPONSE TO --
            // --  WORK AROUND CURRENT CSP STUFF   --
            // this.lookupRoomTimeout = setTimeout(()=> {
            //     this.isRoomLookupBusy = true;
            //     this.isRoomFoundResponse = null;
            //     setTimeout(()=> {
            //         this.isRoomLookupBusy = false;
            //         if(this.address == 'prey') {
              
            //             this.isRoomFoundResponse = true;
            //             this.foundBranding = {"orgId":"421cbea0-b2cd-11e8-bcee-2d8aa185e106","roomId":"194befa0-3cef-11ec-b023-1b0b497b9129","roomName":"Markie's Room","roomCode":"PREY","roomCodeWithoutWhitespace":"PREY","logo":"https://airsquirrels-ditto.s3-us-west-2.amazonaws.com/421cbea0-b2cd-11e8-bcee-2d8aa185e106/assets/logo_connect.png?t=1616539265","deviceType":"SSP","name":"#SQ Mark LLC","bonjour":"121 War Room TV","binaries":{"win32_binary":"https://api.dtto.co/api/rooms/getconnect/32/PREY","win64_binary":"https://api.dtto.co/api/rooms/getconnect/64/PREY","macOS_binary":"https://s3-us-west-2.amazonaws.com/airsquirrels-ditto/421cbea0-b2cd-11e8-bcee-2d8aa185e106/194befa0-3cef-11ec-b023-1b0b497b9129/Ditto.zip"},"helpText":"For more help go to www.google.com","helpDeskPhone":"","helpDeskURL":"","helpDeskEmail":"","backgroundImage":"https://airsquirrels-ditto.s3-us-west-2.amazonaws.com/421cbea0-b2cd-11e8-bcee-2d8aa185e106/assets/backgroundImage_connect.jpeg?t=1616539266","backgroundColor":null,"textColor":"#FFFFFF","customMessage":"Welcome to Squirrels LLC. Click Connect to show your screen to everyone.","branding":{"banner":null,"logo":"https://airsquirrels-ditto.s3-us-west-2.amazonaws.com/421cbea0-b2cd-11e8-bcee-2d8aa185e106/assets/logo_connect.png?t=1616539265","backgroundImage":"https://airsquirrels-ditto.s3-us-west-2.amazonaws.com/421cbea0-b2cd-11e8-bcee-2d8aa185e106/assets/backgroundImage_connect.jpeg?t=1616539266","backgroundColor":null,"textColor":"#FFFFFF","customMessage":"Custom message here","customDomainURL":"123l"},"brandingReceiver":{"banner":null,"logo":"https://airsquirrels-ditto.s3-us-west-2.amazonaws.com/421cbea0-b2cd-11e8-bcee-2d8aa185e106/assets/logo_receiver.png?t=1652889916","backgroundImage":"https://airsquirrels-ditto.s3-us-west-2.amazonaws.com/421cbea0-b2cd-11e8-bcee-2d8aa185e106/assets/backgroundImage_receiver.jpeg?t=1652889916","backgroundColor":"#852c2c","textColor":null,"customMessage":""},"brandingSender":{"banner":null,"logo":null,"backgroundImage":null,"backgroundColor":null,"textColor":null,"customMessage":null},"signage":{"enabled":true,"connectionInfoPosition":"bottomLeft","showConnectionInfo":true},"enableMirroring":true,"enableSignage":false,"enableAlerts":true,"connectionInfoPosition":"bottomLeft","showConnectionInfo":true,"sendUsageStats":true,"deviceSettings":{},"onScreenCodePosition":"Center"};

            //             // Emit branding so we can apply the 
            //             // background, logo & generate a color palette
            //             this.$emit('roomFound', this.foundBranding);
            //         } else {
            //             this.isRoomFoundResponse = false;
            //             this.roomIconVisibilityTimeout = setTimeout(()=> {
            //                 this.isRoomFoundResponse = null;
            //             }, 1500);
            //         }

            //     }, 1000);
            // }, debounceLookup ? 750 : 0);

        },

        onConnectFormSubmit() {
            if(this.appState == 'Connecting' || this.isRoomLookupBusy || !this.isRoomFoundResponse) return;
            // TODO: Lets make sure we actually have a valid room code
            // If they have already selected to continue on the browser, 
            // we shouldnt keep making them choose every time they enter a code.
            // Chromium OS should automatically use the WASM.
            if(this.isContinueOnBrowserFlagged || this.userAgent.os.name == "Chromium OS") {
                if( this.roomReceivers &&  this.roomReceivers.length > 0) {
                    this.connectToSpecificDevices();
                }
                else {
                    this.connect();
                }
            } else if ((this.userAgent.os.name === 'Mac OS' || 
                        this.userAgent.os.name === 'Windows') && 
                        !(this.userAgent.os.name === 'Mac OS' && 'ontouchend' in document)){
                this.$router.push('/info/sharing');
            } else {
                this.$router.push('/info/download');
            } 
        },

        connect() {
            this.failedAllQueries = false;
            this.appState = 'Connecting';
            this.password = '';
            try {
                dittoSender.Connect(this.address);
            } catch (error) {
                console.log('Connect failed', error)
                this.appState = "Ready";
            }
        },

        disconnect(e: any) {
            this.didUserDisconnect = true;

            this.stopMirroring(null);
            dittoSender.Disconnect();
            this.appState = 'Ready';
        },

        senderHasError(error: any) {
            console.log("Sender error: ", error);
        },

        senderDidConnect(name: any) {
            this.receiverName = name.split(',');
        },

        senderDidDisconnect() {
            this.stopMirroring(null);
        },

        senderConfigRetrievalSucceeded(config: any) {
            // console.log("|| senderConfigRetrievalSucceeded() ||");
        },

        senderConfigRetrievalFailed() {
            console.log("|| senderConfigRetrievalFailed() ||");
            if(this.roomIconVisibilityTimeout) {
                clearTimeout(this.roomIconVisibilityTimeout)
                this.roomIconVisibilityTimeout = null;
            }

            this.appState = 'Ready';
            this.isRoomFoundResponse = false;
            this.roomIconVisibilityTimeout = setTimeout(()=> {
                this.isRoomFoundResponse = null;
            }, 1500);
        },

        senderNeedsPassword: function() {
            this.password = '';
            this.appState = 'NeedsPassword';
        },
        
        senderStatusDidChange: function(status: any) {
            console.log('senderStatusDidChange', status)
            let result = '' as string;
            this.failedAllQueries = false;

            switch (status) {
                case DTSenderStatus.None.value:
                case DTSenderStatus.ReadyToConnect.value:
                    result = 'Ready';
                    break;
                case DTSenderStatus.VerifyingConnectionInfo.value:
                case DTSenderStatus.Connecting.value:
                    result = 'Connecting';
                    break;
                case DTSenderStatus.ReadyToMirror.value:
                    result = 'Connected';
                    break;
                case DTSenderStatus.Mirroring.value:
                    result = 'Mirroring';
                    break;
            }
            this.appState = result;
        },
        
        async setUp(module: any) {
            // eslint-disable-next-line no-undef

            DittoSenderModule = module;
            SQLogLevel = module.SQLogLevel;
            DittoSender = module.Wrapper;
            DTSenderStatus = module.DTSenderStatus;

            await DittoSender.SetUp(SQLogLevel.Verbose);
        
            let host = window.location.host;
            let hostSplit = host.split('.');
            let url = `api.${hostSplit[hostSplit.length - 2]}.${hostSplit[hostSplit.length - 1]}`;
            const port = window.location.protocol === 'http:' ? 80 : 443;
            //const port = 443;
            
            dittoSender = await new DittoSender(url, port, false);
              // eslint-disable-next-line no-undef
         if ('MediaStreamTrackProcessor' in window) {
          console.info('MediaStreamTrackProcessor: Is available.');
          this.UseMediaStreamTrackProcessor = true;
          if (MediaRecorder.isTypeSupported('video/webm; codecs=h264,opus')) {
            console.info('H264 & Opus is available.');
          }
          else {
            console.info('H264 & Opus is NOT available.');
            this.UseMediaStreamTrackProcessor = false;
          }
        }
        else {
          console.info('MediaStreamTrackProcessor: Is NOT available.');
          this.UseMediaStreamTrackProcessor = false;
        }
        //Check if we have an encoder to use
        this.checkSupportedVideoCodecs();
            this.appState = 'Ready';
        },
startMirroringProcessor: function(e: any) {
      e.preventDefault();

      this.captureStream = null;
      dittoSender.ResetCapture();
      this.shouldRestart = false;
      this.audioInfoNeedsSent = true;
      this.videoInfoNeedsSent = true;
      this.videoWidth = 0;
      this.videoHeight = 0;
      this.shouldBeRunning = true;

      //Disable PTS diff delay on the receiver
      dittoSender.SetDisableReceiverPTSDiffDelay(true);

      console.info('Starting Mirroring Processor.');

      const constraints = {
        video: true,
        audio: {
          autoGainControl: false,
          channelCount: 2,
          echoCancellation: false,
          noiseSuppression: false,
          latency: 0.002902,
          sampleRate: 48000, //This is often ignored
        },
        systemAudio: "include"
      };

      navigator.mediaDevices.getDisplayMedia(constraints)
      .then(stream => {
        this.captureStream = stream;

        var constraintPromises = [];

        const videoTrack = stream.getVideoTracks()[0];
        console.info("Video Track Details:")
        console.info("-   Settings: " + JSON.stringify(videoTrack.getSettings(), null, 2));
        console.info("-   Constraints: " + JSON.stringify(videoTrack.getConstraints(), null, 2));
        console.info("-   Capabilities: " + JSON.stringify(videoTrack.getCapabilities(), null, 2));

        //For now force 720p, 1080 might be too large for Lower end Chromebook hardware
        var videoConstraints = {
          frameRate: { max: 30 },
          width: 1280,
          height: 720
          //width: 1920,
          //height: 1080
        };

        var videoPromise = videoTrack.applyConstraints(videoConstraints);
        constraintPromises.push(videoPromise);

        videoTrack.onended = function() {
          dittoSender.stopMirroring(null);
        }

        var audioTrack = stream.getAudioTracks()[0];
        if (audioTrack != null) {
          console.info("Audio Track Details:")
          console.info("-   Settings: " + JSON.stringify(audioTrack.getSettings(), null, 2));
          console.info("-   Constraints: " + JSON.stringify(audioTrack.getConstraints(), null, 2));
          console.info("-   Capabilities: " + JSON.stringify(audioTrack.getCapabilities(), null, 2));

          let capabilities = audioTrack.getCapabilities();
          var audioConstraints = {};

          console.info("Applying the following audio constraints: " + JSON.stringify(audioConstraints, null, 2));

          var audioPromise = audioTrack.applyConstraints(audioConstraints);
          constraintPromises.push(audioPromise);

          // Do we need to resample?
          if (audioTrack.getSettings().sampleRate != 48000) {
            console.info('Audio SampleRate: ' + audioTrack.getSettings().sampleRate + ' needs resampled.');
            audioTrack = this.resampleAudioTrack(audioTrack);
            console.info("-   Settings: " + JSON.stringify(audioTrack.getSettings(), null, 2));
          }

          audioTrack.onended = function() {
            dittoSender.stopMirroring(null);
          }
        }

        this.appState = 'Mirroring';

        this.startVideoRead(videoTrack);

        if (audioTrack != null) {
          this.startAudioRead(audioTrack);
        }
      })
      .catch(err => {
          console.log("Failed to get capture: " + err);
      });

    },
     async startAudioRead(audioTrack: any) {
      this.audioWorker = new Worker("AudioWorker.js");
      this.audioWorker.onmessage = (event: any) => {
        if (event.data.type == 'error') {
          this.stopMirroring(null);
          return;
        }
        dittoSender.SendAudioFrame(event.data.buffer);
        event.data.buffer.transfer();
      };
       if ('MediaStreamTrackProcessor' in window){
      this.audioReader = new MediaStreamTrackProcessor({ track: audioTrack }).readable.getReader();
      while(this.shouldBeRunning) {
        const { value: audioData, done } = await this.audioReader.read();
        if (done) {
          console.info('audioFrame is done, exiting.');
          this.stopMirroring(null);
          break;
        } 

        if (this.audioInfoNeedsSent) {
          this.audioInfoNeedsSent = false;
          dittoSender.SetAudioInfo(audioData.sampleRate, audioData.numberOfChannels, audioData.numberOfFrames, audioData.duration);
        }

        this.audioWorker.postMessage({'type':'audioData', 'audioData': audioData}, [audioData]);
        audioData.close();
      }

      this.audioWorker.postMessage({'type':'close'});
      this.audioWorker.terminate();
      }
    },
      async startVideoRead(videoTrack: any) {

      this.videoWorker = new Worker("VideoWorker.js");
      this.videoWorker.onmessage = (event:any) => {
        if (event.data.type == 'error') {
          this.stopMirroring(null);
          return;
        }

        if (this.videoInfoNeedsSent) {
          this.videoInfoNeedsSent = false;
          dittoSender.SetVideoInfo(this.videoWidth, this.videoHeight);
        }

        dittoSender.SendVideoFrame(event.data.buffer, event.data.byteLength);
        event.data.buffer.transfer();
      };

      // Set the video encoder to use
      this.videoWorker.postMessage({'type':'SetVideoEncoder', 'codec':this.videoEncoderToUse});

      this.videoReader = new MediaStreamTrackProcessor({ track: videoTrack }).readable.getReader();
      while (this.shouldBeRunning) {

        const { value: videoFrame, done } = await this.videoReader.read();
        if (done) {
          console.info('videoFrame is done, exiting.');
          this.stopMirroring(null);
          break;
        } 

        if (videoFrame.codedWidth != this.videoWidth || videoFrame.codedHeight != this.videoHeight) {
          this.videoInfoNeedsSent = true;
          console.info('Video Changing from: ' + this.videoWidth + ' x ' + this.videoHeight + ' to ' + videoFrame.codedWidth + ' x ' + videoFrame.codedHeight);
          this.videoWidth = videoFrame.codedWidth;
          this.videoHeight = videoFrame.codedHeight;
        }
        this.videoWorker.postMessage({'type':'videoFrame', 'videoFrame': videoFrame}, [videoFrame]);
        videoFrame.close();
      }

      this.videoWorker.postMessage({'type':'close'});
      this.videoWorker.terminate();
    },
        resampleAudioTrack: function(audioTrack:any) {
      var audioContext = new AudioContext({ sampleRate: 48000 });
      var mediaStream = new MediaStream([audioTrack]); // Wrap track in a stream
      var source = audioContext.createMediaStreamSource(mediaStream);
      var destination = audioContext.createMediaStreamDestination();
      this.audioConverterStream = mediaStream;
      
      source.connect(destination);
  
      var resampledTrack = destination.stream.getAudioTracks()[0];
      return resampledTrack;
    },
        async checkSupportedVideoCodecs() {
      if (!this.UseMediaStreamTrackProcessor) return;
      // Found search chat GPT for "List of Commonly Used H.264 / AVC1 hev1 / hvc1 Codec Strings"
      /*Codec String        | Profile       | Level */
      const codecs = [
        "avc1.42E01E",      // Baseline      | 3.0
        "avc1.42E028",      // Baseline      | 4.0
        "avc1.4D401E",      // Main          | 3.0
        "avc1.4D401F",      // Main          | 3.1
        "avc1.4D4028",      // Main          | 4.0
        "avc1.64001E",      // High          | 3.0
        "avc1.640028",      // High          | 4.0
        "avc1.640032",      // High          | 5.0
        //"hev1.1.6.L93.B0",  // Main          | 4.1
        //"hev1.2.4.L120.B0", // Main 10       | 5.2
        //"hvc1.1.6.L93.B0",  // Main          | 5.1
        //"hvc1.2.4.L150.B0", // Main 10       | 6.0
        //"hvc1.2.4.L153.B0", // Main 10       | 6.3
        //"vp8",
        //"vp9",              // 0
        //"vp09.00.10.08",    // 0
        //"vp09.00.10.10",    // 0
        //"vp09.01.10.10",    // 1
        //"vp09.02.10.10",    // 2
      ];

      const accelerations = ["prefer-hardware", "prefer-software"];
      
      await codecs.forEach(async codec => {
        accelerations.forEach(async accel => {
          var config = {codec, hardwareAcceleration:accel, width: 1280, height: 720};
          const support = await VideoEncoder.isConfigSupported(config)
          if (support.supported) {
            console.log('Supported (' + accel + '): ' + config.codec);
            if (config.codec == this.defaultVideoEncoder) {
              this.videoEncoderToUse = this.defaultVideoEncoder;
            }
            else if (this.FindNextAvailableVideoEncoder) {
              this.videoEncoderToUse = config.codec;
              this.FindNextAvailableVideoEncoder = false;
            }
          } else {
            //console.log('Not supported: ', config.codec);
          }
        });
      });


      if (this.videoEncoderToUse == null && !this.FindNextAvailableVideoEncoder) {
        this.FindNextAvailableVideoEncoder = true;
        this.checkSupportedVideoCodecs();
      }
      else if (this.videoEncoderToUse == null && this.FindNextAvailableVideoEncoder) {
        console.error('Failed to find a video encoder to use');
        this.UseMediaStreamTrackProcessor = false;
      }

    },
        startMirroring(e: any) {
      if (this.UseMediaStreamTrackProcessor) {
        this.startMirroringProcessor(e);
        return;
      }

      e.preventDefault();

       this.captureStream = null;

            dittoSender.ResetCapture();

            // JB I don't see options for view in the constraints object.
            // const options = {
            //     view: {
            //         cursor: "always"
            //     },
            //     audio: false
            // };

            // JB This should tell us what is available in the constraints object
            // const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
            
            // added audio to the constraings
            // const constraints = {
                // video: {
                //     width: { min: 1280, ideal: 1920, max: 1920 },
                //     height: { min: 720, ideal: 1080,  max: 1080 },
                // },
            //     audio: false
            // }; 

            const constraints = {
                video: true,
                audio: {
                    autoGainControl: false,
                    channelCount: 2,
                    echoCancellation: false,
                    noiseSuppression: false,
                    latency: 0.01,
                    // sampleRate: 48000,
                },
                systemAudio: "include"
            }

            //(navigator.mediaDevices as any).getDisplayMedia(options, constraints).then((stream: any) => {
            // JB based on the documentation there is not "options" parameter. (*https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getDisplayMedia)
            navigator.mediaDevices.getDisplayMedia(constraints).then(async (stream: any) => {
                this.captureStream = stream;

                var constraintPromises = [];

                const videoTrack = stream.getVideoTracks()[0];
                console.info("-   Settings: " + JSON.stringify(videoTrack.getSettings(), null, 2));
                console.info("-   Constraints: " + JSON.stringify(videoTrack.getConstraints(), null, 2));
                console.info("-   Capabilities: " + JSON.stringify(videoTrack.getCapabilities(), null, 2));

                var videoConstraints = {
                    frameRate: { max: 30 }
                };

                var videoPromise = videoTrack.applyConstraints(videoConstraints);
                constraintPromises.push(videoPromise);

                videoTrack.onended = () => {
                    this.stopMirroring(null);
                }

                const audioTrack = stream.getAudioTracks()[0];

                if (audioTrack != null) {
                    console.info("Audio Track Details:")
                    console.info("-   Settings: " + JSON.stringify(audioTrack.getSettings(), null, 2));
                    console.info("-   Constraints: " + JSON.stringify(audioTrack.getConstraints(), null, 2));
                    console.info("-   Capabilities: " + JSON.stringify(audioTrack.getCapabilities(), null, 2));

                    let capabilities = audioTrack.getCapabilities();

                    var audioConstraints = {};

                    if (capabilities.channelCount && capabilities.channelCount.max >= 2) {
                    // audioConstraints["channelCount"] = 2;
                    }

                    if (capabilities.latency) {
                    // audioConstraints["latency"] = capabilities.latency.min;
                    }

                    console.info("Applying the following audio constraints: " + JSON.stringify(audioConstraints, null, 2));

                    var audioPromise = audioTrack.applyConstraints(audioConstraints);
                    constraintPromises.push(audioPromise);
                }

                await Promise.all(constraintPromises);

                let userAgent = window.navigator.userAgent.toLowerCase();
                let options = {} as any;

                if (userAgent.indexOf("edge") > -1 || userAgent.indexOf("edg") > -1) { // MS Edge
                    options = { mimeType: "video/webm; codecs=h264" };
                } else if (userAgent.indexOf("chrome") > -1) {
                    options = { mimeType: "video/webm; codecs=h264" };
                } else {
                    options = { mimeType: "video/mp4" };
                }

                options.audioBitrateMode = 'constant';
                options.bitsPerSecond = 5000000;

                this.streamRecorder = new MediaRecorder(this.captureStream, options);

                this.streamRecorder.ondataavailable = (event: any) => {
                    if (event.data.size == 0) return;

                    event.data.arrayBuffer().then((data: any) => {
                        dittoSender.HandleData(data);
                    });
                }

                this.streamRecorder.start(2);

                this.appState = 'Mirroring';
            })
            .catch((err: any) => {
                console.log("Failed to get capture: " + err);
            });
        },

        stopMirroring: function(e: any) {
            if (e != null) {
                e.preventDefault();
            }
            this.shouldBeRunning = false;
            
            if (this.streamRecorder != null) {
                this.streamRecorder.stop();
                this.streamRecorder = null;
            }

            if (this.captureStream != null) {
                let tracks = this.captureStream.getTracks();

                tracks.forEach((track: any) => track.stop());

                this.captureStream = null;
            }
        // New Capture cleanup
      if (this.audioConverterStream != null) {
        let tracks = this.audioConverterStream.getTracks();
        tracks.forEach((track:any) => track.stop());
        this.audioConverterStream = null;
      }

      this.videoReader = null;
      this.audioReader = null;

      dittoSender.EndStream();

      this.videoInfoNeedsSent = true;
      this.audioInfoNeedsSent = true;

      if (this.appState != 'Ready')
        this.appState = 'Connected';
        },

        submitPassword(e: any) {
            e.preventDefault();
            dittoSender.SetPassword(this.passwordInput);
            this.appState = 'Connecting';
        },

        receiverCheckChanged:function(e: any) {
            if(this.roomReceivers.filter((x: any) => !x.sspQueryFailed).length == this.roomReceivers.filter((x: any) => x.specificConnect).length) {
                this.isSelectAllEnabled = true;
            }
            else {
                this.isSelectAllEnabled = false;
            }
            let device = e.currentTarget._value;
            dittoSender.ToggleReceiverSpecificConnect(device.ip, device.specificConnect);
            this.$forceUpdate();
        },

        toggleSelectAllReceivers(e: any)  {
            this.roomReceivers.forEach((recv: any) => {
                if(!recv.sspQueryFailed) {
                    recv.specificConnect = this.isSelectAllEnabled;
                }
                else {
                    recv.specificConnect = false;
                }
                dittoSender.ToggleReceiverSpecificConnect(recv.ip, recv.specificConnect);
            });
            this.$forceUpdate();
        },

        connectToSpecificDevices() {
            this.appState = 'Connecting';
            this.password = '';
            try {
                dittoSender.ConnectToSpecificDevices();
            } catch (error) {
                console.log('connectToSpecificDevices failed', error);
                this.appState = "Ready";
            }
        },

        cancelReceiverSelection(e: any) {
            this.appState = 'Ready';
            e.preventDefault();
        },

        senderReceiversListUpdated: function(receivers: any) {
            var result = JSON.parse(receivers);
            this.roomReceivers = result;
            //If there is only 1 receiver just connect right away skip receiver selection
            if (this.roomReceivers.length > 1) {
                this.appState = "Ready";
                if(this.roomReceivers.filter((x:any) => !x.sspQueryFailed).length > 0) {
                    this.isSelectAllEnabled = true;
                }
                this.toggleSelectAllReceivers(this.roomReceivers);
            }
            else if (this.roomReceivers.length == 1) {
                this.connectToSpecificDevices();
            }
            else {
                console.log('No receivers found...');
            }
        },

        senderFinishedQueries: function() {
            console.log("sender finished queries.");
        },

        onResize() {
            this.windowWidth = window.innerWidth;
        }
    },

    created() {
        // Set flag so the appState watcher will know
        // to do the room lookup when it is ready.
        if(this.isRoomCodeConnectView) {
            this.isInitialRoomCodeFetch = true;
        }
        
        const parser = new UAParser();
        this.userAgent = parser.getResult();
        this.osName = this.userAgent.os.name;
        
        (this as any).roomCodeInput = this.$route.params.roomcode;

        // Only initialize the WASM 
        if(this.loadWasm) {
            this.appState = "Loading";
            createDittoSenderModule().then((loadedModule: any) => {
                this.setUp(loadedModule);
            });
        } else {
            this.appState = "Suspended";
        }
    },

    mounted() {
        this.$nextTick(() => {
        window.addEventListener('resize', this.onResize);
        })
    },

    beforeMount(){
        // Reroute people if they hit the download/sharing f
        // screens without first having put in a room code.
        if(!this.address && (this.isDownloadView || this.isSharingView)) {
            this.$router.push('/');
        }

        window.removeEventListener('resize', this.onResize); 
        
        // ------------------------------------------
        // --- Organization Landing Page Branding --- 
        // ------------------------------------------

        // If we're on an org page, load the branding for the org from the get-go
        if(this.isOrganizationConnectView) {
            const splitLocationHref = window.location.href.split('/');
            const organizationLandindId = splitLocationHref[splitLocationHref.length - 1];

            const host = window.location.host;
            const hostSplit = host.split('.');
            const url = `api.${hostSplit[hostSplit.length - 2]}.${hostSplit[hostSplit.length - 1]}`;
            
            axios.get(`https://${ url }/api/organizations/branding/${ organizationLandindId }`).then((resp: any)=> {
                this.organizationId = resp.data.branding.organization.id;
                this.organizationName = resp.data.name;
                this.foundBranding = resp.data.branding;
                this.foundRoomName = this.foundBranding.name;
                this.$emit('roomFound', this.foundBranding);
            })
            .catch((error: any)=> {
                console.log("We have an error: ", error.toJSON());
            });
        }
    },

    beforeRouteUpdate(to, from) {
        (this as any).roomCodeInput = to.params.roomcode;
    },

    components: {
        InlineTextLoader,
        wasm,
        Download,
        Sharing
    },

    watch: {
        address(value: string) {
            if(dittoSender) {
                const isValid = dittoSender.IsAddressValid(value);
                this.isAddressValid = isValid;
            }
        },

        appState(value: string) {
            switch (value) {
                case "Ready":
                    if(this.isInitialRoomCodeFetch) {
                        this.isInitialRoomCodeFetch = false;
                        const add = this.$route.params.roomCode as string;
                        this.address = add;
                        this.lookupRoom(null, false);
                    }
                    this.isPasswordRequired = false;
                    break;
                
                case "Connected":
                    this.isPasswordRequired = false;
                    this.didUserConnect = true;
                    break;
                
                case "NeedsPassword":
                    this.isPasswordRequired = true;
                    break;

                case "Suspended":
                    if(this.isInitialRoomCodeFetch) {
                        this.isInitialRoomCodeFetch = false;
                        const add = this.$route.params.roomCode as string;
                        this.address = add;
                        this.lookupRoom(null, false);
                    }
                    break;

                default:
                    break;
            }
        }
    }
});
//         $.ajax({
//             contentType: 'application/json',
//             url: `${window.API_HOST}/api/rooms/lookup/`,
//             type: "POST",
//             data: JSON.stringify({
//                 "roomCode": code.toUpperCase(),
//                 "orgID": $('#orgID')[0].value ? $('#orgID')[0].value  : null
//             }),
//             error: (xhr: any, ajaxOptions: any, thrownError: any) => {
//                 if (xhr.status == 404) {
//                     codeStatus.removeClass('valid').addClass('invalid').show().css('opacity',1);
//                     // -- hide the net step container
//                     this.resetConnectionInformation();
//                 }
//                 else if (xhr.status == 403) {
//                     codeStatus.removeClass('valid').addClass('invalid').show();
//                     alert("This room is not currently available. Please contact the account administrator.");
//                     this.resetConnectionInformation();
//                     return false;
//                 }
//                 else if (xhr.status == 402) {
//                     codeStatus.removeClass('valid').addClass('invalid').show();
//                     this.resetConnectionInformation();
//                     alert(xhr.responseText);
//                     return false;
//                 }
//             }
//         })
//             .done((roomObject: any) => {
//                 resolve(roomObject);
//                 codeStatus.removeClass('invalid').addClass('valid');
//                 // -- Google Analytics -----
//                 // if (typeof ga !== "undefined" && ga !== null) {
//                 //     ga('send', 'pageview', '/connect/info');
//                 // }
//                 this.refreshConnectionInformation(roomObject);
//             })
// submitConnectFormHandler() {
//     const code = this.$el.find('#connectCode').val();
//     const roomInfo = this.$el.find('#roomInfo');

//     if (code.length < 6) {
//         // -- Make validation icon appear
//         // this.$el.find('#codeValidationStatus').fadeTo(700, 0);
//         this.resetConnectionInformation();
//         // -- Hide the nextStepContainer
//     }
//     else {
//         this.retrieveConferenceInfo(code);
//     }
//     return false;
// },

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "3b4ce838": (_ctx.getBrandingTextColor),
  "96131012": (_ctx.getBrandingColorHslNormal),
  "d21ba2f4": (_ctx.getBrandingColorHslDark),
  "b9be9c9a": (_ctx.getBrandingColorHslDarker),
  "595bc614": (_ctx.getBrandingColorHslCSV)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__